import React from 'react';
import PropTypes from 'prop-types';
import isFunction from 'lodash/isFunction';
import _merge from 'lodash/merge';
import { selectors as i18nSelectors, Message } from 'mage-i18n';
import { withResponsive } from 'mage-components';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import get from 'lodash/get';
import { SeparatorSecondary, SpacerVertical } from '@klarna/bubble-ui';
import { formUtils, moetPropTypes, analytics } from 'moet-core';

import {
  getMerchantCountryKey,
  getMerchantAwareTranslator,
  getMerchantEntityType,
  merchantRegion,
} from '@/selectors/merchant';

import FieldSetWithAddressForm from '../FieldSetWithAddressForm';
import { LinkWithoutHref } from '../LinkWithoutHref';
import RoleToggle from '../RoleToggle';
import PersonalIdInput from '../PersonalIdInput';
import { getFields } from './fields';

const tPrefix = 'products-fe.components.stakeholders.form';

const style = {
  rightAlignment: {
    textAlign: 'right',
  },
};

const _PersonForm = ({
  onBlur,
  onChange,
  stakeholder,
  stakeholder: {
    address: { country },
  },
  isDesktop,
  autoFocus = false,
  availableRoles = [],
  name = 'person-form',
  isDisabled = false,
  disabledRoles = [],
  merchantCountry,
  merchantEntityType,
  onRemove = null,
  onChangeRoles = null,
  tabIndexStart = 0,
  tMerchantAware,
  deploymentRegion,
  onTouchPersonalId,
}) => {
  const conditionalFieldProps = {
    firstName: {
      size: isDesktop ? 0.5 : 1,
      extraProps: { autoFocus },
    },
    lastName: {
      size: isDesktop ? 0.5 : 1,
    },
    personalId: {
      component: PersonalIdInput,
      extraProps: {
        onTouchPersonalId,
      },
    },
  };

  const translateField = (field, fieldName) => ({
    ...field,
    label: tMerchantAware(`${tPrefix}.${fieldName}.label`),
    validators: (field.validators ? field.validators : []).map(validator => ({
      ...validator,
      errorMessage: tMerchantAware(
        isFunction(validator.errorMessage)
          ? validator.errorMessage({ country: country.value })
          : validator.errorMessage,
      ),
    })),
    extraProps: {
      ...field.extraProps,
      id: `${fieldName}_${name}`,
      name: `${fieldName}_${name}`,
    },
  });

  const onBlurFields = (key, fields) => {
    const stakeholderKey = get(fields, `${key}.stakeholderKey`);
    return onBlur(stakeholderKey || key);
  };

  const onChangeFields = (key, val, fields) => {
    const stakeholderKey = get(fields, `${key}.stakeholderKey`);
    return onChange(stakeholderKey || key, val);
  };

  const ANALYTICS_EVENTS = {
    removeOne: new analytics.Event({
      name: 'One Person Removed',
      description: 'Merchant removes a person',
      action: analytics.EVENT_ACTION.CTA_CLICK,
      category: analytics.EVENT_CATEGORY.FLOW_STEP,
      type: analytics.EVENT_TYPE.USER_PREFERENCE,
      properties: ['path'],
    }),
  };

  const onRemoveWithAnalytics = () => {
    analytics.logEvent(ANALYTICS_EVENTS.removeOne, { path: window.location.pathname.toString() });
    return onRemove();
  };

  const baseFields = getFields(stakeholder, deploymentRegion, merchantCountry, merchantEntityType);

  const fields = Object.keys(baseFields).reduce((translatedFields, fieldKey) => {
    const { name, ...fieldProps } = baseFields[fieldKey];
    const additionalProps = conditionalFieldProps[fieldKey] || {};
    return {
      ...translatedFields,
      [fieldKey]: translateField(_merge(fieldProps, additionalProps), name),
    };
  }, {});

  const numberFields = Object.keys(baseFields).length;

  return (
    <>
      <div style={{ zIndex: '1' }}>
        <FieldSetWithAddressForm
          isBorderless
          tabIndexStart={tabIndexStart}
          isDisabled={isDisabled}
          name={name}
          fields={formUtils.initForm(fields, stakeholder)}
          onBlur={onBlurFields}
          onChange={onChangeFields}
        />
      </div>
      {availableRoles.map((role, index) => (
        <React.Fragment key={role}>
          <SpacerVertical small />
          <RoleToggle
            tabIndex={tabIndexStart + numberFields + index}
            role={role}
            stakeholderRoles={stakeholder.roles}
            onChangeRoles={onChangeRoles}
            isDisabled={isDisabled || disabledRoles.includes(role)}
          />
        </React.Fragment>
      ))}
      <SpacerVertical extraSmall />
      {onRemove !== null && (
        <div style={style.rightAlignment}>
          {availableRoles && availableRoles.length > 0 && <SeparatorSecondary />}
          <SpacerVertical extraExtraSmall />
          <LinkWithoutHref
            textToken="text-style/text/labels/body/medium"
            onPress={e => {
              e.preventDefault();
              onRemoveWithAnalytics();
            }}
            UNSAFE_props={{ tabIndex: tabIndexStart + numberFields + availableRoles.length }}
          >
            <Message path={`${tPrefix}.remove`} />
          </LinkWithoutHref>
        </div>
      )}
    </>
  );
};

_PersonForm.propTypes = {
  deploymentRegion: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  merchantCountry: PropTypes.string.isRequired,
  stakeholder: PropTypes.shape({
    address: moetPropTypes.addressPropType,
    dateOfBirth: moetPropTypes.dateFieldPropType,
    firstName: moetPropTypes.textFieldPropType,
    hasPersonalId: PropTypes.bool,
    isPersonalIdPristine: moetPropTypes.boolFieldPropType,
    lastName: moetPropTypes.textFieldPropType,
    personalId: moetPropTypes.textFieldPropType,
    reactFormId: PropTypes.string,
    roles: PropTypes.array,
  }).isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onTouchPersonalId: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  availableRoles: PropTypes.arrayOf(PropTypes.string),
  disabledRoles: PropTypes.arrayOf(PropTypes.string),
  isDisabled: PropTypes.bool,
  merchantEntityType: PropTypes.string,
  name: PropTypes.string,
  tabIndexStart: PropTypes.number,
  onChangeRoles: PropTypes.func,
  onRemove: PropTypes.func,
};

export const PersonForm = compose(
  connect(state => ({
    tMerchantAware: getMerchantAwareTranslator(state),
    language: i18nSelectors.language(state),
    merchantCountry: getMerchantCountryKey(state),
    deploymentRegion: merchantRegion(state),
    merchantEntityType: getMerchantEntityType(state),
  })),
)(withResponsive(_PersonForm));
